import { GOOGLE_CONFIG, DRIVE_CONFIG, MIME_TYPES } from '../utils/config';

class GoogleDriveService {
  constructor() {
    this.accessToken = null;
    this.isInitialized = false;
    this.onAuthError = null; // Callback for auth errors
  }

  // Method to set auth error callback from Auth component
  setAuthErrorCallback(callback) {
    this.onAuthError = callback;
  }

  // Clear all session data and trigger re-authentication
  clearSession() {
    this.accessToken = null;
    localStorage.removeItem('drillDocs_user');
    localStorage.removeItem('drillDocs_token');
    localStorage.removeItem('drillDocs_token_timestamp');
    localStorage.removeItem('drillDocs_token_expiry');
    localStorage.removeItem('drillDocs_lastScopeCheck');
    sessionStorage.clear();
    
    // Notify Auth component to update user state
    if (this.onAuthError) {
      this.onAuthError('Session expired. Please sign in again.');
    }
  }

  async initialize() {
    if (this.isInitialized) return;
    
    return new Promise((resolve, reject) => {
      if (typeof window.google === 'undefined') {
        reject(new Error('Google Identity Services not loaded'));
        return;
      }

      // Initialize Google Identity Services
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CONFIG.CLIENT_ID,
        callback: this.handleSignIn.bind(this)
      });

      this.isInitialized = true;
      resolve();
    });
  }

  handleSignIn(response) {
    // This will be called when user signs in
    console.log('User signed in:', response);
  }

  async getAccessToken(forceReauth = false) {
    return new Promise((resolve, reject) => {
      // Check if we have a valid token in storage
      const storedToken = localStorage.getItem('drillDocs_token');
      const tokenTimestamp = localStorage.getItem('drillDocs_token_timestamp');
      
      // ALWAYS use stored token if it exists and not forcing reauth
      if (storedToken && !forceReauth) {
        const now = Date.now();
        const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
        
        // Only check 30-day limit, ignore Google's 1-hour expiry
        if (tokenTimestamp) {
          const tokenAge = now - parseInt(tokenTimestamp);
          if (tokenAge < thirtyDaysInMs) {
            console.log('🔄 Using stored token (valid for 30 days)');
            this.accessToken = storedToken;
            resolve(this.accessToken);
            return;
          } else {
            console.log('⏰ Token older than 30 days, need fresh login');
          }
        } else {
          // No timestamp, use the token anyway
          console.log('🔄 Using stored token (no expiry check)');
          this.accessToken = storedToken;
          resolve(this.accessToken);
          return;
        }
      }

      if (this.accessToken && !forceReauth) {
        resolve(this.accessToken);
        return;
      }

      // Check if Google APIs are loaded
      if (typeof window.google === 'undefined' || !window.google.accounts) {
        reject(new Error('Google APIs not loaded yet. Please sign in first using the button in the top-right.'));
        return;
      }

      console.log('🔑 Requesting Google Drive access token with expanded scopes...');

      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CONFIG.CLIENT_ID,
        scope: GOOGLE_CONFIG.SCOPES.join(' '),
        callback: (tokenResponse) => {
          console.log('📥 Token response received:', tokenResponse);
          if (tokenResponse.access_token) {
            this.accessToken = tokenResponse.access_token;
            const now = Date.now();
            
            // Store the token with timestamp and expiry info
            localStorage.setItem('drillDocs_token', tokenResponse.access_token);
            localStorage.setItem('drillDocs_token_timestamp', now.toString());
            
            // Google tokens typically expire in 1 hour (3600 seconds)
            // But we'll use them for up to 30 days if possible
            const expiryTime = tokenResponse.expires_in ? 
              now + (tokenResponse.expires_in * 1000) : 
              now + (60 * 60 * 1000); // Default 1 hour
            localStorage.setItem('drillDocs_token_expiry', expiryTime.toString());
            
            console.log('✅ Access token obtained and stored successfully');
            console.log(`🕐 Token expires at: ${new Date(expiryTime).toLocaleString()}`);
            resolve(this.accessToken);
          } else if (tokenResponse.error) {
            console.error('❌ OAuth error:', tokenResponse.error);
            reject(new Error(`OAuth error: ${tokenResponse.error}`));
          } else {
            console.error('❌ No access token received');
            reject(new Error('No access token received'));
          }
        },
      });
      
      // Use different prompt strategies based on situation
      let promptOption = '';
      if (forceReauth) {
        promptOption = 'consent'; // Force fresh consent
      } else if (!storedToken) {
        promptOption = 'select_account'; // Show account picker on first sign-in
      }
      // If we have a stored token but it's expired, don't force consent (try silent refresh)
      
      console.log(`🔐 Auth prompt strategy: ${promptOption || 'none (silent refresh)'}`);
      
      tokenClient.requestAccessToken({ 
        prompt: promptOption,
        include_granted_scopes: true, // Include previously granted scopes
        enable_granular_consent: true, // Allow incremental consent
      });
    });
  }

  // Helper method to handle API responses with proper error handling
  async handleApiResponse(response, requestDetails = '') {
    console.log(`📥 Drive API Response [${requestDetails}]:`, response.status, response.statusText);

    if (response.status === 401) {
      console.log('⚠️ Got 401 but keeping token - Google tokens can work beyond expiry');
      // Don't clear session - keep using the token
      // Many Google tokens continue working even after "expiry"
    }

    if (response.status === 403) {
      console.log('⚠️ 403 error - may be quota or permissions');
      const errorText = await response.text();
      if (errorText.includes('quotaExceeded')) {
        throw new Error('API quota exceeded. Please try again later.');
      }
      // Don't clear session for 403 errors either
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Drive API Error Details:', errorText);
      throw new Error(`Drive API error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  async searchFiles(query, options = {}) {
    const token = await this.getAccessToken();
    
    const driveId = options.driveId || DRIVE_CONFIG.SHARED_DRIVE_ID;
    
    const params = new URLSearchParams({
      q: query,
      corpora: 'drive',
      driveId: driveId,
      supportsAllDrives: 'true',
      includeItemsFromAllDrives: 'true',
      fields: options.fields || 'files(id,name,mimeType)',
      orderBy: options.orderBy || 'name'
    });

    const url = `https://www.googleapis.com/drive/v3/files?${params}`;
    console.log('🔍 Drive API Request:', url);
    console.log('🔑 Using token:', token.substring(0, 20) + '...');

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    const data = await this.handleApiResponse(response, `searchFiles: ${query}`);
    console.log('📊 Drive API Data:', data);
    return data;
  }

  async findFolder(folderName) {
    const query = `name='${folderName}' and mimeType='${MIME_TYPES.FOLDER}' and trashed=false`;
    const result = await this.searchFiles(query);
    return result.files.find(f => f.name === folderName);
  }

  async getFolderContents(folderId) {
    const query = `'${folderId}' in parents and trashed=false`;
    return this.searchFiles(query, {
      orderBy: 'folder,name',
      fields: 'files(id,name,mimeType,parents)'
    });
  }

  async getTechnicalProcesses() {
    try {
      console.log('🎯 Loading Technical Processes from Google Drive...');
      
      // Try to find the folder
      let technicalProcessesFolder;
      try {
        technicalProcessesFolder = await this.findFolder(DRIVE_CONFIG.TECHNICAL_PROCESSES_FOLDER);
      } catch (sharedDriveError) {
        console.warn('⚠️ Shared drive search failed, trying regular search...', sharedDriveError);
        // Fallback: try without shared drive parameters
        technicalProcessesFolder = await this.findFolderRegular(DRIVE_CONFIG.TECHNICAL_PROCESSES_FOLDER);
      }
      
      if (!technicalProcessesFolder) {
        throw new Error('Technical Processes folder not found. Please ensure the folder exists and you have access to it.');
      }

      console.log(`✅ Found Technical Processes folder: ${technicalProcessesFolder.id}`);

      const subfolders = await this.getFolderContents(technicalProcessesFolder.id);
      const folders = subfolders.files.filter(f => f.mimeType === MIME_TYPES.FOLDER);

      console.log(`📁 Found ${folders.length} process folders:`, folders.map(f => f.name));

      return folders;
    } catch (error) {
      console.error('❌ Error loading Technical Processes:', error);
      throw error;
    }
  }

  async getOperationalProcedures() {
    return this.getSharedDriveFolders('Operational Procedures', DRIVE_CONFIG.SHARED_DRIVES.OPERATIONAL_PROCEDURES, 'Operational Procedures');
  }

  async getOnboardingDocumentation() {
    return this.getSharedDriveFolders('Onboarding Documentation', DRIVE_CONFIG.SHARED_DRIVES.ONBOARDING_DOCUMENTATION, 'Onboarding Documentation');
  }

  async getKnowledgeBase() {
    return this.getSharedDriveFolders('Knowledge Base', DRIVE_CONFIG.SHARED_DRIVES.KNOWLEDGE_BASE, 'Knowledge Base');
  }

  async getSharedDriveFolders(sectionName, driveId, folderName) {
    try {
      console.log(`🎯 Loading ${sectionName} from Google Drive...`);
      
      if (folderName) {
        // Look for a specific folder name inside the shared drive, then get its subfolders
        const query = `name='${folderName}' and mimeType='${MIME_TYPES.FOLDER}' and trashed=false`;
        const result = await this.searchFiles(query, { 
          driveId: driveId,
          fields: 'files(id,name,mimeType,parents)'
        });

        const mainFolder = result.files?.find(f => f.name === folderName);
        
        if (!mainFolder) {
          console.log(`⚠️ No '${folderName}' folder found in shared drive, trying to list all root folders...`);
          // Fallback: get all folders in the shared drive root
          const rootQuery = `mimeType='${MIME_TYPES.FOLDER}' and trashed=false`;
          const rootResult = await this.searchFiles(rootQuery, { 
            driveId: driveId,
            orderBy: 'name',
            fields: 'files(id,name,mimeType,parents)'
          });
          const folders = rootResult.files || [];
          console.log(`📁 Found ${folders.length} root folders in ${sectionName}:`, folders.map(f => f.name));
          return folders;
        }

        console.log(`✅ Found ${folderName} folder: ${mainFolder.id}`);

        // Get subfolders inside the main folder
        const subfolders = await this.getFolderContents(mainFolder.id);
        const folders = subfolders.files.filter(f => f.mimeType === MIME_TYPES.FOLDER);

        console.log(`📁 Found ${folders.length} subfolders in ${folderName}:`, folders.map(f => f.name));
        return folders;
      } else {
        // Get all folders in the shared drive root
        const query = `mimeType='${MIME_TYPES.FOLDER}' and trashed=false`;
        const result = await this.searchFiles(query, { 
          driveId: driveId,
          orderBy: 'name',
          fields: 'files(id,name,mimeType,parents)'
        });

        const folders = result.files || [];
        console.log(`📁 Found ${folders.length} folders in ${sectionName}:`, folders.map(f => f.name));
        return folders;
      }
    } catch (error) {
      console.error(`❌ Error loading ${sectionName}:`, error);
      throw error;
    }
  }

  // Fallback method without shared drive parameters
  async findFolderRegular(folderName) {
    const query = `name='${folderName}' and mimeType='${MIME_TYPES.FOLDER}' and trashed=false`;
    const token = await this.getAccessToken();
    
    const params = new URLSearchParams({
      q: query,
      fields: 'files(id,name,mimeType)'
    });

    const url = `https://www.googleapis.com/drive/v3/files?${params}`;
    console.log('🔍 Regular Drive API Request:', url);

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    const data = await this.handleApiResponse(response, `findFolderRegular: ${folderName}`);
    console.log('📊 Regular Drive API Data:', data);
    return data.files.find(f => f.name === folderName);
  }

  async getProcessFolderContents(processName, driveId = null) {
    try {
      console.log(`🎯 Loading process: ${processName}`);
      
      let processFolder;
      
      if (driveId) {
        // Search within specific shared drive
        const query = `name='${processName}' and mimeType='${MIME_TYPES.FOLDER}' and trashed=false`;
        const result = await this.searchFiles(query, { driveId });
        processFolder = result.files?.find(f => f.name === processName);
      } else {
        // Fallback to original method
        processFolder = await this.findFolder(processName);
      }
      
      if (!processFolder) {
        throw new Error(`${processName} folder not found`);
      }

      console.log(`✅ Found folder: ${processFolder.name} (${processFolder.id})`);

      const contents = await this.getFolderContents(processFolder.id);
      
      return {
        folder: processFolder,
        contents: contents.files
      };
    } catch (error) {
      console.error('Drive loading error:', error);
      throw error;
    }
  }

  // Helper method to get drive ID from route
  getDriveIdFromRoute(route) {
    const driveMap = {
      'technical-processes': DRIVE_CONFIG.SHARED_DRIVES.TECHNICAL_PROCESSES,
      'operational-procedures': DRIVE_CONFIG.SHARED_DRIVES.OPERATIONAL_PROCEDURES,
      'onboarding-documentation': DRIVE_CONFIG.SHARED_DRIVES.ONBOARDING_DOCUMENTATION,
      'knowledge-base': DRIVE_CONFIG.SHARED_DRIVES.KNOWLEDGE_BASE
    };
    return driveMap[route];
  }

  async getFolderDocuments(folderId) {
    const documentTypes = [
      MIME_TYPES.DOCUMENT,
      MIME_TYPES.SPREADSHEET,
      MIME_TYPES.PRESENTATION,
      MIME_TYPES.MARKDOWN,
      MIME_TYPES.TEXT,
      MIME_TYPES.PDF,
      MIME_TYPES.PNG,
      MIME_TYPES.JPG,
      MIME_TYPES.GIF,
      MIME_TYPES.SVG
    ];
    
    const query = `'${folderId}' in parents and trashed=false and (${documentTypes.map(type => `mimeType='${type}'`).join(' or ')})`;
    return this.searchFiles(query);
  }

  getEmbedUrl(docId, mimeType) {
    switch (mimeType) {
      case MIME_TYPES.DOCUMENT:
        return `https://docs.google.com/document/d/${docId}/preview`;
      case MIME_TYPES.SPREADSHEET:
        return `https://docs.google.com/spreadsheets/d/${docId}/preview`;
      case MIME_TYPES.PRESENTATION:
        return `https://docs.google.com/presentation/d/${docId}/preview`;
      case MIME_TYPES.MARKDOWN:
      case MIME_TYPES.TEXT:
      case MIME_TYPES.PDF:
      case MIME_TYPES.PNG:
      case MIME_TYPES.JPG:
      case MIME_TYPES.GIF:
      case MIME_TYPES.SVG:
        return `https://drive.google.com/file/d/${docId}/preview`;
      default:
        return `https://drive.google.com/file/d/${docId}/preview`;
    }
  }

  getDocumentType(mimeType) {
    switch (mimeType) {
      case MIME_TYPES.SPREADSHEET:
        return 'sheet';
      case MIME_TYPES.PRESENTATION:
        return 'slides';
      case MIME_TYPES.MARKDOWN:
        return 'markdown';
      case MIME_TYPES.TEXT:
        return 'text';
      case MIME_TYPES.PDF:
        return 'pdf';
      case MIME_TYPES.PNG:
      case MIME_TYPES.JPG:
      case MIME_TYPES.GIF:
      case MIME_TYPES.SVG:
        return 'image';
      default:
        return 'doc';
    }
  }
}

export default new GoogleDriveService();